import { useState, useEffect } from "react";
import { downloadFile } from "@/lib/api";
import { Download, FileText, FileSpreadsheet, Loader2 } from "lucide-react";
import { toast } from "sonner";
import Disclaimer from "@/components/Disclaimer";
import XeroPreviewCard from "@/components/XeroPreviewCard";

export default function ExportPage() {
  const [busy, setBusy] = useState("");

  // Lift the Assistant FAB while on the Export screen so it never sits
  // on top of the yellow "Exports are informational only" disclaimer at
  // the bottom of the page. Cleaned up on unmount so other routes keep
  // the default 6rem clearance.
  useEffect(() => {
    document.documentElement.style.setProperty("--fab-bottom", "11rem");
    return () => {
      document.documentElement.style.removeProperty("--fab-bottom");
    };
  }, []);

  const grab = async (kind, path, filename) => {
    setBusy(kind);
    try {
      await downloadFile(path, filename);
      toast.success(`${kind.toUpperCase()} downloaded`);
    } catch (e) {
      toast.error(e.message || "Download failed");
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="font-heading text-2xl font-extrabold">Export</h1>
        <div className="text-sm text-white/60">Send data to your accountant or software.</div>
      </div>

      <button
        data-testid="export-csv"
        disabled={!!busy}
        onClick={() => grab("csv", "/api/export/csv", "buildintel-export.csv")}
        className="w-full bi-surface p-5 flex items-center gap-4 hover:brightness-110 active:scale-[0.99] transition disabled:opacity-60"
      >
        <div className="w-12 h-12 rounded-lg bg-[#FF9500]/15 text-[#FF9500] flex items-center justify-center"><FileSpreadsheet className="w-6 h-6" /></div>
        <div className="flex-1 text-left">
          <div className="font-heading font-bold text-lg">CSV export</div>
          <div className="text-sm text-white/60">All entries with GST, category and job — Excel/Google Sheets ready.</div>
        </div>
        {busy === "csv" ? <Loader2 className="w-5 h-5 text-[#FF9500] animate-spin" /> : <Download className="w-5 h-5 text-white/60" />}
      </button>

      <button
        data-testid="export-pdf"
        disabled={!!busy}
        onClick={() => grab("pdf", "/api/export/pdf", "buildintel-accountant-pack.pdf")}
        className="w-full bi-surface p-5 flex items-center gap-4 hover:brightness-110 active:scale-[0.99] transition disabled:opacity-60"
      >
        <div className="w-12 h-12 rounded-lg bg-[#FF9500]/15 text-[#FF9500] flex items-center justify-center"><FileText className="w-6 h-6" /></div>
        <div className="flex-1 text-left">
          <div className="font-heading font-bold text-lg">PDF Accountant Pack</div>
          <div className="text-sm text-white/60">One-page summary with BAS draft — email to your accountant.</div>
        </div>
        {busy === "pdf" ? <Loader2 className="w-5 h-5 text-[#FF9500] animate-spin" /> : <Download className="w-5 h-5 text-white/60" />}
      </button>

      <div className="grid grid-cols-2 gap-2">
        <div data-testid="export-xero" className="bi-surface p-4 flex items-center gap-3 opacity-70 select-none">
          <div className="w-10 h-10 rounded-lg bg-white text-black font-heading font-extrabold flex items-center justify-center">X</div>
          <div className="text-left flex-1">
            <div className="font-heading font-bold">Xero</div>
            <div className="text-[11px] text-white/50">Coming later</div>
          </div>
        </div>
        <div data-testid="export-myob" className="bi-surface p-4 flex items-center gap-3 opacity-70 select-none">
          <div className="w-10 h-10 rounded-lg bg-[#7A2FBF] text-white font-heading font-extrabold flex items-center justify-center">M</div>
          <div className="text-left flex-1">
            <div className="font-heading font-bold">MYOB</div>
            <div className="text-[11px] text-white/50">Coming later</div>
          </div>
        </div>
      </div>

      <XeroPreviewCard />

      {/* Trailing spacer keeps the yellow disclaimer above the Assistant
          FAB (bottom-24 default). Same pattern used on other Tax screens. */}
      <div aria-hidden className="h-20" data-testid="export-fab-clearance" />

      <Disclaimer text="Exports are informational only and not lodgement to the ATO. Please review with your accountant before submitting anything." />
    </div>
  );
}
